import { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import type { ProfilesListFilters, ProfileSummaryResponse } from '@/types/scoring.types';

interface CandidatosFiltersProps {
  filters: ProfilesListFilters;
  summary: ProfileSummaryResponse | null;
  onFiltersChange: (next: ProfilesListFilters) => void;
}

const LEVEL_OPTIONS = ['', 'PLATINUM', 'GOLD', 'SILVER', 'BRONZE', 'NONE'];

export function CandidatosFilters({ filters, summary, onFiltersChange }: CandidatosFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.search ?? '');

  // Debounced search server-side: manda el texto como filter.search al backend.
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === (filters.search ?? '')) return; // evita loops
    const t = setTimeout(
      () => onFiltersChange({ ...filters, search: trimmed || undefined, page: 1 }),
      250,
    );
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const total = summary?.total_profiles ?? 0;
  const eligible = summary?.eligible_count ?? 0;
  const ineligible = summary?.ineligible_count ?? 0;
  // Ofrecibles = elegibles sin credito activo (banner_state='ELIGIBLE' en el pipeline).
  const ofrecibles =
    summary?.pipeline?.by_banner_state?.find((s) => s.state === 'ELIGIBLE')?.count ?? 0;

  type Pill = 'ALL' | 'ELIGIBLE' | 'OFRECIBLE' | 'INELIGIBLE';

  const activePill: Pill =
    filters.banner_state === 'ELIGIBLE' ? 'OFRECIBLE'
    : filters.is_eligible === 'YES' ? 'ELIGIBLE'
    : filters.is_eligible === 'NO' ? 'INELIGIBLE'
    : 'ALL';

  function setPill(next: Pill) {
    const base = { ...filters, page: 1 };
    switch (next) {
      case 'ALL':
        onFiltersChange({ ...base, is_eligible: undefined, banner_state: undefined });
        break;
      case 'ELIGIBLE':
        onFiltersChange({ ...base, is_eligible: 'YES', banner_state: undefined });
        break;
      case 'OFRECIBLE':
        // Ofrecible = elegible + banner ELIGIBLE. Mandamos ambos para que el filtro
        // sea explicito aunque hoy banner ELIGIBLE ya implica is_eligible=YES.
        onFiltersChange({ ...base, is_eligible: 'YES', banner_state: 'ELIGIBLE' });
        break;
      case 'INELIGIBLE':
        onFiltersChange({ ...base, is_eligible: 'NO', banner_state: undefined });
        break;
    }
  }

  function setLevel(value: string) {
    onFiltersChange({ ...filters, credit_level: value || undefined, page: 1 });
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <PillButton active={activePill === 'ALL'} onClick={() => setPill('ALL')}>
        Todos · {total}
      </PillButton>
      <PillButton active={activePill === 'ELIGIBLE'} onClick={() => setPill('ELIGIBLE')}>
        Elegibles · {eligible}
      </PillButton>
      <PillButton active={activePill === 'OFRECIBLE'} onClick={() => setPill('OFRECIBLE')}>
        Ofrecibles · {ofrecibles}
      </PillButton>
      <PillButton active={activePill === 'INELIGIBLE'} onClick={() => setPill('INELIGIBLE')}>
        No Elegibles · {ineligible}
      </PillButton>

      <select
        value={filters.credit_level ?? ''}
        onChange={(e) => setLevel(e.target.value)}
        className="rounded-md border border-border bg-card px-2.5 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
      >
        {LEVEL_OPTIONS.map((l) => (
          <option key={l || 'all'} value={l}>
            {l === '' ? 'Todos los niveles' : l}
          </option>
        ))}
      </select>

      <div className="flex w-64 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1">
        <Search size={12} className="shrink-0 text-muted-foreground" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Buscar nombre o telefono..."
          className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground focus:outline-none"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => setSearchInput('')}
            className="shrink-0 text-muted-foreground hover:text-foreground"
            aria-label="Limpiar búsqueda"
          >
            <X size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

function PillButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
